import * as primitives from "@mirage/service-typeahead-search/service/primitives";
import { CacheKey } from "@mirage/service-typeahead-search/service/typeahead-cache";
import {
  SourceId,
  typeahead,
} from "@mirage/service-typeahead-search/service/types";
import * as wrappers from "@mirage/service-typeahead-search/service/utils/wrappers";
import { PeopleRecommendation } from "@mirage/shared/search/people-recommendations";
import { isDefined } from "@mirage/shared/util/tiny-utils";
import * as rx from "rxjs";
import * as sourceScore from "../scoring/source-score";
import { twoStageSearch } from "../search/two-stage-search";
import { getResultSection } from "../utils/section-mapper";
import { getSourceLimit } from "../utils/source-helpers";

/**
 * In the performance branch that is waiting to be merged this is changed,
 * We can safely set to 8 right now because we presort in the cache
 * and 8 is the max we can ever show.
 */
const FILTER_RESULT_LIMIT = 50;

export const raw: typeahead.TaggedSearch = (
  query,
  mode,
  { cache, config, grossLimit, sectionLimit },
) => {
  const sourceLimit = getSourceLimit(
    typeahead.ResultType.PeopleFilter,
    query,
    grossLimit,
    sectionLimit,
  );

  const sectionId = getResultSection(
    typeahead.ResultType.PeopleFilter,
    mode,
    query,
    config,
  );

  return rx.defer(() => {
    const peopleRecommendations = cache.all(
      CacheKey.PeopleRecommendations,
    ) as PeopleRecommendation[];

    return rx.of(peopleRecommendations).pipe(
      rx.map((allPeopleRecommendations) => {
        const candidates = twoStageSearch<PeopleRecommendation>(
          query,
          allPeopleRecommendations.map((peopleRecommendation) => ({
            searchableStrings: [
              ...(peopleRecommendation.displayName
                ? [
                    ...peopleRecommendation.displayName.split(" "),
                    peopleRecommendation.displayName,
                  ]
                : []),
              peopleRecommendation.email,
            ].filter(isDefined),
            originalResult: peopleRecommendation,
          })),
          { limit: FILTER_RESULT_LIMIT, isPeopleFilters: true },
        ).map((searchableResult) => searchableResult.originalResult);

        const taggedResults = candidates.map((candidate) => {
          return primitives.peopleFilter(candidate.uuid, candidate, {
            sectionId,
          });
        });

        return taggedResults
          .map((taggedResult) => sourceScore.score(query, config)(taggedResult))
          .sort((a, b) => b.score - a.score)
          .slice(0, sourceLimit);
      }),
      rx.mergeMap((results) => rx.from(results)),
    );
  });
};

export const search = wrappers.wrapped(SourceId.AskSuggestions, raw);
